import { CarryOutOutlined, PhoneOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { memo } from 'react';

import { Avatar } from '@app/components/Avatar';
import { TruncatedHeaderText } from '@app/shared/components';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import { getLeadInfoInitials } from '../lib/getLeadInfoInitials';

import type { LeadsRegistryLeadInfoViewModel } from '../../../../model/types';
import type { FC } from 'react';

import './LeadInfoCell.scss';

interface LeadInfoCellProps {
  leadInfo: LeadsRegistryLeadInfoViewModel;
}

const INCOMING_DIRECTION_MARKER = '↙';
const OUTGOING_DIRECTION_MARKER = '↗';

const LeadInfoCellComponent: FC<LeadInfoCellProps> = ({ leadInfo }) => {
  const {
    avatarUrl,
    clientName,
    clientPhone,
    socialIntegrationType,
    lastMessage,
    crmTaskMessage,
    lastMessageTime,
    hasUnreadMessage,
    isNoAnswer,
    isIncoming,
    isOutgoing,
  } = leadInfo;

  // Лид поднят по наступлению задачи: показываем иконку задачи и её текст
  const hasCrmTask = Boolean(crmTaskMessage);

  return (
    <div
      className={cn('LeadInfoCell', {
        'LeadInfoCell--incoming': isIncoming,
        'LeadInfoCell--outgoing': isOutgoing,
        // Голубой фон означает «неотвечено», точка — «непрочитано».
        // Классы исторически названы наоборот, переименование — отдельной задачей.
        'LeadInfoCell--unread': isNoAnswer,
      })}
    >
      {/* Full view — shown when the client column is at its normal width */}
      <div className="LeadInfoCell__full">
        <div className="LeadInfoCell__main-row">
          {(hasUnreadMessage || lastMessageTime) && (
            <span className="LeadInfoCell__time-group">
              {hasUnreadMessage && (
                <span
                  className="LeadInfoCell__no-answer-dot"
                  aria-hidden="true"
                />
              )}
              {lastMessageTime && (
                <span className="LeadInfoCell__time">{lastMessageTime}</span>
              )}
            </span>
          )}
          <Avatar
            className="LeadInfoCell__avatar"
            src={avatarUrl}
            nameFirst={clientName}
            size={32}
            round
          />

          <div className="LeadInfoCell__client">
            <div className="LeadInfoCell__name-row">
              <TruncatedHeaderText
                className="LeadInfoCell__name"
                lineCount={1}
                text={clientName}
              />
            </div>

            <TruncatedHeaderText
              className="LeadInfoCell__phone"
              lineCount={1}
              text={clientPhone ?? ''}
            />
          </div>
        </div>

        <div className="LeadInfoCell__message-row">
          {(isIncoming || isOutgoing) && (
            <span className="LeadInfoCell__direction" aria-hidden="true">
              {isIncoming
                ? INCOMING_DIRECTION_MARKER
                : OUTGOING_DIRECTION_MARKER}
            </span>
          )}
          {hasCrmTask ? (
            <CarryOutOutlined
              className="LeadInfoCell__task-icon"
              aria-label="Задача"
            />
          ) : (
            <SocialIntegrationIcon
              type={socialIntegrationType}
              size="sm"
              className="LeadInfoCell__integration"
            />
          )}
          <TruncatedHeaderText
            className="LeadInfoCell__message"
            lineCount={1}
            text={crmTaskMessage ?? lastMessage ?? ''}
          />
        </div>
      </div>

      {/* Compact view — shown when the table is scrolled right and the column
          shrinks; screen readers skip this duplicate via aria-hidden */}
      <div className="LeadInfoCell__compact-view" aria-hidden="true">
        {/* Left column: avatar, then direction + integration icons centered below */}
        <div className="LeadInfoCell__compact-left">
          <Avatar src={avatarUrl} nameFirst={clientName} size={32} round />
          <div className="LeadInfoCell__compact-icons">
            {(isIncoming || isOutgoing) && (
              <span className="LeadInfoCell__direction" aria-hidden="true">
                {isIncoming
                  ? INCOMING_DIRECTION_MARKER
                  : OUTGOING_DIRECTION_MARKER}
              </span>
            )}
            {hasCrmTask ? (
              <CarryOutOutlined className="LeadInfoCell__task-icon" />
            ) : (
              <SocialIntegrationIcon
                type={socialIntegrationType}
                size="sm"
                className="LeadInfoCell__integration"
              />
            )}
          </div>
        </div>
        {/* Right column: initials on row 1, phone icon on row 2 */}
        <div className="LeadInfoCell__compact-right">
          <div className="LeadInfoCell__compact-name">
            {hasUnreadMessage && (
              <span
                className="LeadInfoCell__no-answer-dot"
                aria-hidden="true"
              />
            )}
            <span className="LeadInfoCell__initials">
              {getLeadInfoInitials(clientName)}
            </span>
          </div>
          {clientPhone && (
            <div className="LeadInfoCell__compact-phone">
              <PhoneOutlined className="LeadInfoCell__phone-icon-compact" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const LeadInfoCell = memo(LeadInfoCellComponent);
